import React, { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IAutocompleteOption } from 'transfix-ui/components/Autocomplete'
import {
  Autosuggest,
  getAutosuggestOptions,
  IAutosuggestWrapperProps,
} from 'transfix-ui/components/Autosuggest'
import { getUserTags } from 'utils/actions/tags'
import { isTagLoading, selectTags } from 'utils/reducers/tags'

export interface ITagResults {
  nickname: string
  firstName: string
  lastName: string
  id: string
  firstChildId: string
  type: string
  text?: string
}

interface ITagAutosuggestProps extends IAutosuggestWrapperProps<ITagResults> {
  disabled?: boolean
  withAvatar?: boolean
  groups?: ('users' | 'groups')[]
}

/**
 * Lists tags for users & groups pulled from the `tagReducer`
 */
const TagAutosuggest: FC<ITagAutosuggestProps> = ({
  disabled = false,
  onChange: onChangeProp,
  placeholder: placeholderProp = 'Search for user or group',
  value: valueProp,
  withAvatar = false,
  groups = ['users', 'groups'],
}) => {
  const dispatch = useDispatch()
  const tagList = useSelector(selectTags)
  const isLoadingTags = useSelector(isTagLoading)
  const [tagSuggestions, setTagSuggestions] = useState<IAutocompleteOption[]>([])

  // Load tags and set to autosuggest option format for type 'group' and 'user'
  useEffect(() => {
    if (!tagList.length && !isLoadingTags) {
      dispatch(getUserTags())

      return
    }

    if (isLoadingTags) return
    let options: IAutocompleteOption[] = []

    if (groups.includes('users')) {
      const allUsers = tagList.filter((tag: ITagResults) => tag.lastName.length)
      const userList = getAutosuggestOptions({
        options: allUsers,
        groupIndex: 1,
        groupName: 'User',
        mapOption: (tag: ITagResults) => ({
          id: tag.firstChildId,
          text: `${tag.firstName} ${tag.lastName}`,
        }),
      })
      options = [...options, ...userList]
    }

    if (groups.includes('groups')) {
      const allGroups = tagList.filter((tag: ITagResults) => !tag.lastName)
      const groupsList = getAutosuggestOptions({
        options: allGroups,
        groupIndex: 2,
        groupName: 'Group',
        mapOption: (tag: ITagResults) => ({
          id: tag.id,
          text: `${tag.firstName}`,
        }),
      })
      options = [...options, ...groupsList]
    }
    setTagSuggestions(options)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingTags])

  return (
    <Autosuggest
      {...(withAvatar ? { renderOptionType: 'withAvatar' } : {})}
      value={valueProp}
      options={tagSuggestions}
      onChange={onChangeProp}
      placeholder={placeholderProp}
      clearable
      large
      dataTest='tag-autosuggest'
      disabled={disabled}
    />
  )
}

export default TagAutosuggest
